<template>
  <!--
    ArtifactDetail SECTION — Metric artifact, "Definition" tab.
    
    WHAT: Combines Methodology (what the metric calculates) and Expectation (what
      healthy results should look like) into a single streamlined, progressive
      interface instead of a giant form. Supports live click-to-edit, visual
      benchmark chips, structured calculation rules, and AI generation/completion.
    
    CONTRACT (props { record, artifact, context }):
      READS  record.composable_config.methodology
             record.composable_config.expectation
      EMITS  patch({ composable_config: { ...config, methodology, expectation } })
             clone-then-overlay: preserves existing keys on both objects.
      POST   generate-contract/ { mode: 'derive' | 'methodology' | 'expectation' }
  -->
  <div class="space-y-5">
    <!-- Header: Action & Staleness Bar -->
    <div class="flex flex-wrap items-center justify-between gap-3 bg-white border border-gray-200 rounded-xl px-5 py-4">
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <span class="text-sm font-bold text-gray-900">Metric Definition</span>
          <span
            v-if="hasMethodology && hasExpectation"
            class="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
          >
            <font-awesome-icon :icon="['fas', 'check']" class="text-[9px]" /> Complete
          </span>
          <span
            v-else
            class="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
          >
            Draft
          </span>
        </div>
        <p class="text-xs text-gray-500 mt-0.5">
          The unified specification for calculation logic, clinical scope, and audit expectations.
        </p>
      </div>

      <!-- AI Action -->
      <button
        class="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3.5 py-2 hover:opacity-90 disabled:opacity-50 transition-opacity whitespace-nowrap"
        :style="{ background: '#2563EB' }"
        :disabled="running"
        @click="triggerAI('definition')"
      >
        <font-awesome-icon
          :icon="['fas', running ? 'circle-notch' : 'wand-magic-sparkles']"
          :class="running ? 'fa-spin' : ''"
          class="text-[10px]"
        />
        {{ running ? 'Generating…' : 'Generate with AI' }}
      </button>
    </div>

    <!-- Staleness Alert Banner -->
    <div
      v-if="isStale && hasMethodology"
      class="flex items-start gap-2.5 px-4 py-3 rounded-xl border text-xs leading-relaxed bg-amber-50 border-amber-200 text-amber-800"
    >
      <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mt-0.5 flex-shrink-0 text-amber-600" />
      <div class="flex-1">
        <span class="font-semibold">Definition out of sync:</span>
        This metric's SQL steps or data sources have changed since this specification was generated.
        Click <strong>Derive Full Definition</strong> to sync with current logic.
      </div>
    </div>

    <!-- Status Message Banner -->
    <div
      v-if="msg"
      class="text-xs rounded-xl px-4 py-3 border flex items-center justify-between"
      :class="isError ? 'text-red-700 bg-red-50 border-red-200' : 'text-blue-800 bg-blue-50 border-blue-200'"
    >
      <span>{{ msg }}</span>
      <button v-if="!running" class="text-gray-400 hover:text-gray-600 ml-2" @click="msg = ''">×</button>
    </div>

    <!-- Core Purpose -->
    <div class="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
      <div class="text-xs font-bold uppercase tracking-wider text-gray-500 border-b border-gray-100 pb-3">Core Purpose</div>

      <!-- Intent & Goals -->
      <label class="block text-[11px] font-semibold text-gray-700 uppercase tracking-wide">Intent & Goals
        <textarea v-model="form.intent" rows="3" class="mt-1 block w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 font-normal normal-case tracking-normal autogrow" placeholder="Why this metric exists and what action it drives"></textarea>
      </label>

      <!-- Grain -->
      <label class="block text-[11px] font-semibold text-gray-700 uppercase tracking-wide">Grain
        <textarea v-model="form.grain" rows="3" class="mt-1 block w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 font-normal normal-case tracking-normal autogrow" placeholder="What one row represents"></textarea>
      </label>

      <!-- Direction -->
      <label class="block text-[11px] font-semibold text-gray-700 uppercase tracking-wide">Direction
        <textarea v-model="form.direction" rows="3" class="mt-1 block w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 font-normal normal-case tracking-normal autogrow" placeholder="Higher is better / Lower is better"></textarea>
      </label>

      <!-- Data Sources (optional) -->
      <div>
        <label class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide block mb-1">Data Sources (optional)</label>
        <div class="flex flex-wrap items-center gap-1.5">
          <span
            v-for="(src, i) in form.data_sources"
            :key="src"
            class="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 text-slate-800 font-mono text-[11px] px-2 py-0.5 rounded"
          >
            <span>{{ src }}</span>
            <button class="text-slate-400 hover:text-red-500 font-bold ml-0.5 leading-none" @click.prevent="removeSource(i)">×</button>
          </span>
          <input
            v-model="newSource"
            type="text"
            placeholder="+ SCHEMA.TABLE"
            class="text-[11px] font-mono px-2 py-0.5 border border-dashed border-gray-300 rounded bg-transparent focus:outline-none focus:border-blue-400 w-36"
            @keydown.enter.prevent="addSource"
          />
        </div>
      </div>
    </div>

    <!-- Calculation Rules -->
    <div class="bg-white border border-gray-200 rounded-xl p-5 space-y-2">
      <label class="block text-[11px] font-semibold text-gray-700 uppercase tracking-wide">Calculation Rules</label>
      <textarea
        v-model="form.summary"
        rows="10"
        class="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 autogrow-lg"
        placeholder="Describe how this metric is calculated — population, denominator, numerator, exclusions, timing, caveats. Free-form."
      ></textarea>
    </div>

    <!-- Audit Expectations -->
    <div class="bg-white border border-gray-200 rounded-xl p-5 space-y-2">
      <label class="block text-[11px] font-semibold text-gray-700 uppercase tracking-wide">Audit Expectations</label>
      <textarea
        v-model="form.audit_expectations"
        rows="10"
        class="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400 autogrow-lg"
        placeholder="What healthy results look like — expected rate range, denominator scale, year-over-year trends, entity spread, known outliers, red flags. Free-form."
      ></textarea>
    </div>

    <!-- Human Notes & Authority Overrides -->
    <div class="bg-sky-50/60 border border-sky-200 rounded-xl p-4 space-y-2">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <font-awesome-icon :icon="['fas', 'circle-info']" class="text-sky-600 text-xs" />
          <span class="text-xs font-bold uppercase tracking-wider text-sky-800">Human Notes & Authoritative Quirks</span>
        </div>
        <button
          v-if="editingField !== 'human_notes'"
          class="text-xs font-semibold text-sky-700 hover:text-sky-900"
          @click="edit('human_notes')"
        >
          Edit Notes
        </button>
      </div>
      <p class="text-[11px] text-sky-700/80 leading-relaxed">
        Specific instructions, practice quirks, or manual rules. The automated audit judge treats these notes as authoritative ground truth that overrides automated heuristics.
      </p>
      <textarea
        v-if="editingField === 'human_notes'"
        ref="humanNotesEditor"
        v-model="form.human_notes"
        rows="4"
        class="w-full text-xs border border-sky-300 rounded-lg p-2.5 focus:outline-none bg-white autogrow"
        placeholder="One instruction or practice quirk per line"
        @blur="editingField = null"
      ></textarea>
      <ul v-else-if="asList(form.human_notes).length" class="space-y-1 pt-1 cursor-pointer" @click="edit('human_notes')">
        <li v-for="(note, i) in asList(form.human_notes)" :key="i" class="text-xs text-sky-900 flex gap-2">
          <span class="text-sky-400 select-none">•</span>
          <span>{{ note }}</span>
        </li>
      </ul>
      <p v-else class="text-xs text-sky-600/60 italic pt-1 cursor-pointer" @click="edit('human_notes')">
        No human notes added yet. Click to record practice rules or overrides.
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { getJob } from '@/services/api/personalAnalyst';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const POLL_MS = 5000;
const MAX_POLLS = 120;

export default {
  name: 'MetricDefinitionSection',
  components: { FontAwesomeIcon },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, default: () => ({}) },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch', 'refresh'],
  data() {
    return {
      form: this.hydrate(),
      hydrating: true,
      editingField: null,
      isStale: false,
      running: false,
      msg: '',
      isError: false,
      newSource: '',
      pollCount: 0,
    };
  },
  computed: {
    methodology() {
      return (this.record.composable_config || {}).methodology || null;
    },
    hasMethodology() {
      return !!this.methodology;
    },
    expectation() {
      return (this.record.composable_config || {}).expectation || null;
    },
    hasExpectation() {
      return !!this.expectation;
    },


  },
  watch: {
    form: {
      deep: true,
      handler() {
        if (this._hydrationToken !== this._liveToken) return;
        this.emit();
      },
    },
    'record.composable_config': {
      deep: true,
      handler() {
        if (this.running || this.editingField) return;
        this.rehydrate();
      },
    },
  },
  created() {
    this._liveToken = 0;
    this._hydrationToken = 0;
    this._baseline = { ...this.form, data_sources: [...(this.form.data_sources || [])] };
  },
  mounted() {
    this.loadMeta();
    this._hydrationToken = this._liveToken;
    this.hydrating = false;
  },
  beforeUnmount() {
    clearTimeout(this._pollTimer);
  },
  methods: {
    edit(field) {
      this.editingField = field;
      this.$nextTick(() => {
        const refName = field + 'Editor' || field + 'Input';
        const el = Array.isArray(this.$refs[refName]) ? this.$refs[refName][0] : this.$refs[refName];
        if (el) el.focus();
      });
    },
    lines(v) {
      if (Array.isArray(v)) return v.join('\n');
      return v ? String(v) : '';
    },
    unlines(s) {
      return String(s || '').split('\n').map((x) => x.trim()).filter(Boolean);
    },
    asList(s) {
      return this.unlines(s);
    },
    num(v) {
      if (v === '' || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isNaN(n) ? v : n;
    },
    addSource() {
      const v = this.newSource.trim();
      if (!v) return;
      if (!this.form.data_sources.includes(v)) this.form.data_sources.push(v);
      this.newSource = '';
    },
    removeSource(i) {
      this.form.data_sources.splice(i, 1);
    },
    hydrate() {
      const cc = this.record.composable_config || {};
      const m = cc.methodology || {};
      const e = cc.expectation || {};

      return {
        // Methodology fields
        summary: m.summary || '',
        grain: Array.isArray(m.grain) ? m.grain.join(' ') : (m.grain || ''),
        direction: Array.isArray(m.direction) ? m.direction.join(' ') : (m.direction || ''),
        population: this.lines(m.population),
        denominator: this.lines(m.denominator),
        numerator: this.lines(m.numerator),
        timing: this.lines(m.timing),
        exclusions: this.lines(m.exclusions),
        caveats: this.lines(m.caveats),
        data_sources: Array.isArray(m.data_sources) ? [...m.data_sources] : [],

        // Expectation fields
        intent: e.intent || '',
        human_notes: this.lines(e.human_notes),
        audit_expectations: e.audit_expectations || '',
      };
    },
    rehydrate() {
      this._liveToken += 1;
      const token = this._liveToken;
      this.hydrating = true;
      this.form = this.hydrate();
      this._baseline = { ...this.form, data_sources: [...(this.form.data_sources || [])] };
      this.$nextTick(() => {
        if (this._liveToken !== token) return;
        this._hydrationToken = token;
        this.hydrating = false;
      });
    },
    emit() {
      if (this.hydrating) return;
      const f = this.form;
      const cc = this.record.composable_config || {};

      // Overlay methodology
      const m = { ...(cc.methodology || {}) };
      m.summary = f.summary || null;
      m.grain = f.grain || null;
      m.direction = f.direction || null;
      for (const k of ['population', 'denominator', 'numerator', 'timing', 'exclusions', 'caveats']) {
        m[k] = this.unlines(f[k]);
      }
      m.data_sources = [...f.data_sources];

      // Overlay expectation
      const e = { ...(cc.expectation || {}) };
      e.intent = f.intent || null;
      e.human_notes = this.unlines(f.human_notes);
      e.audit_expectations = f.audit_expectations || null;

      this.$emit('patch', {
        composable_config: {
          ...cc,
          methodology: m,
          expectation: e,
        },
      });
    },
    async loadMeta() {
      try {
        const { data } = await axios.get(
          `api/impact_analytics/composable-metrics/${this.record.id}/contract/`
        );
        this.isStale = !!data.is_stale;
        const lj = data.latest_job;
        const ageMin = lj ? (Date.now() - new Date(lj.created_at).getTime()) / 60000 : Infinity;
        if (lj && ['QUEUED', 'PROCESSING'].includes(lj.status) && ageMin < 10) {
          this.running = true;
          this.aiProgressText = 'AI is writing definition…';
          this.pollCount = 0;
          this.schedulePoll(lj.id);
        }
      } catch {
        // staleness is advisory
      }
    },
    async triggerAI() {
      this.running = true;
      this.isError = false;
      this.msg = 'Generating definition — this takes ~15–60s.';
      this.emit();
      await new Promise((r) => setTimeout(r, 600));
      try {
        const { data } = await axios.post(
          `api/impact_analytics/composable-metrics/${this.record.id}/generate-contract/`,
          { mode: 'definition' }
        );
        if (data.status === 'FAILED') throw new Error('The job failed to start.');
        this.pollCount = 0;
        this.schedulePoll(data.job_id);
      } catch (e) {
        this.running = false;
        this.isError = true;
        this.msg = `Generation failed: ${e?.response?.data?.error || e.message}`;
      }
    },
    schedulePoll(jobId) {
      clearTimeout(this._pollTimer);
      this._pollTimer = setTimeout(() => this.poll(jobId), POLL_MS);
    },
    async poll(jobId) {
      this.pollCount += 1;
      try {
        const job = await getJob(jobId);
        if (job.status === 'COMPLETED') {
          this.running = false;
          this.msg = 'Definition updated successfully.';
          this.isError = false;
          this.$emit('refresh');
          return;
        }
        if (job.status === 'FAILED') {
          this.running = false;
          this.isError = true;
          this.msg = `Generation failed: ${job.error_message || 'The worker did not complete.'}`;
          return;
        }
      } catch {
        // transient poll error
      }
      if (this.pollCount >= MAX_POLLS) {
        this.running = false;
        this.isError = true;
        this.msg = 'Generation timed out — please check back shortly.';
        return;
      }
      this.schedulePoll(jobId);
    },
  },
};
</script>

<style scoped>
textarea.autogrow {
  field-sizing: content;
  min-height: 4.8rem;
  max-height: 50vh;
  resize: vertical;
  overflow-y: auto;
}
textarea.autogrow-lg {
  field-sizing: content;
  min-height: 15rem;
  max-height: 60vh;
  resize: vertical;
  overflow-y: auto;
}
</style>
